import { max, min, sum } from 'lodash';

/**
 * CVX-6554: summary-report "Data to Include" as measures x treatments.
 *
 * A measure is something a report can include (a property, time to fix, PPE cost); a treatment
 * is how its values are combined within each period (average, minimum, ...). Each chosen
 * measure+treatment is one flat column option, so saved reports (a flat list of column ids),
 * the graph and the table code need no change.
 *
 * Backward compatibility: a column id that existed before keeps its exact meaning and its own
 * func/label - it is passed in `legacy` for the treatment it always had. New treatments get
 * `<measure id>__<treatment>` ids ('__' rather than ':' because column ids also end up in
 * element ids and jQuery selectors).
 */
export type Treatment = 'avg' | 'min' | 'max' | 'sum' | 'count';

export const TreatmentLabelKeys: Record<Treatment, string> = {
  avg: 'REPORTING.Treatment_Average',
  min: 'REPORTING.Treatment_Minimum',
  max: 'REPORTING.Treatment_Maximum',
  sum: 'REPORTING.Treatment_Total',
  count: 'REPORTING.Treatment_Count'
};

/** Combine a period's values; '' when there is nothing to combine (0 is a real value), 0 for count. */
export function aggregate(values: number[], treatment: Treatment): number | '' {
  if (treatment === 'count') {
    return values.length;
  }
  if (!values.length) {
    return '';
  }
  switch (treatment) {
    case 'min':
      return min(values);
    case 'max':
      return max(values);
    case 'sum':
      return sum(values);
    default:
      return sum(values) / values.length;
  }
}

export interface Measure {
  id: string;
  label: string;
  treatments: Treatment[];
  /** the values one group/period contributes; only used for treatments without a legacy column */
  values: (collection: any[]) => number[];
  /** shape an aggregated value for display (rounding, durations); not applied to '' */
  format?: (value: number, treatment: Treatment) => any;
  /** existing column options, by the treatment they already compute */
  legacy?: Partial<Record<Treatment, any>>;
  cellType?: string;
  showWhen?: any;
}

/** Flatten a measure into one column option per treatment. */
export function expandMeasure(measure: Measure, translate: (key: string) => string): any[] {
  return measure.treatments.map((treatment) => {
    const treatmentLabel = translate(TreatmentLabelKeys[treatment]);
    const tags = { measure: measure.id, measureLabel: measure.label, treatment, treatmentLabel };
    const legacy = measure.legacy?.[treatment];

    if (legacy) {
      return { ...legacy, ...tags };
    }

    const label = `${measure.label}: ${treatmentLabel}`;
    return {
      id: `${measure.id}__${treatment}`,
      description: label,
      label,
      units: label,
      cellType: measure.cellType || 'number',
      showWhen: measure.showWhen ?? true,
      func: (collection: any[]) => {
        const value = aggregate(measure.values(collection || []), treatment);
        return value === '' || !measure.format ? value : measure.format(value, treatment);
      },
      ...tags
    };
  });
}
